<template>
  <el-dialog
    v-model="visible"
    title="用户登录"
    width="440px"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :show-close="true"
    custom-class="login-dialog"
    @closed="onDialogClosed"
    @opened="onDialogOpened"
  >
    <div class="login-box">
      <div class="login-header">
        <h2>简笔奇迹素材网</h2>
      </div>
      <el-form :model="loginForm" @submit.prevent="handleLogin" class="login-form">
        <el-form-item>
          <el-input 
            v-model="loginForm.username" 
            placeholder="请输入用户名"
            :disabled="loading"
          >
            <template #prefix>
              <el-icon><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item>
          <el-input 
            v-model="loginForm.password" 
            :type="passwordVisible ? 'text' : 'password'" 
            placeholder="请输入密码"
            :disabled="loading"
            @keyup.enter="handleLogin"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
            <template #suffix>
              <el-icon class="password-icon" @click="passwordVisible = !passwordVisible">
                <component :is="passwordVisible ? 'View' : 'Hide'" />
              </el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item>
          <div class="login-options">
            <el-checkbox v-model="loginForm.remember">记住密码</el-checkbox>
            <el-button 
              v-if="savedCredentialsExist" 
              type="text" 
              size="small" 
              class="clear-saved-btn"
              @click="clearSavedCredentials">
              清除已保存信息
            </el-button>
          </div>
        </el-form-item>
        
        <div v-if="loginForm.remember" class="security-tip">
          <el-icon><Warning /></el-icon>
          <span>请勿在公共设备上使用此功能</span>
        </div>

        <el-form-item>
          <el-button 
            type="primary" 
            :loading="loading"
            @click="handleLogin"
            class="login-button"
          >
            登录
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
// 从Element Plus获取图标
import { User, Lock, View, Hide, Warning } from '@element-plus/icons-vue'
// 引入自定义认证状态管理
import authState from '@/utils/authState'

// 简单的加密解密函数（仅用于基本混淆，不是强安全加密）
const encryptPassword = (password) => {
  try {
    return btoa(password); // 使用Base64编码
  } catch (e) {
    console.error('加密密码失败:', e);
    return '';
  }
};

const decryptPassword = (encryptedPassword) => {
  try {
    return atob(encryptedPassword); // 使用Base64解码
  } catch (e) {
    console.error('解密密码失败:', e);
    return '';
  }
};

// 定义props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  redirectUrl: {
    type: String,
    default: ''
  }
})

// 定义事件
const emit = defineEmits(['update:show', 'login-success', 'login-error'])

// 本地状态
const visible = ref(false)
const router = useRouter()
const loading = ref(false)
const passwordVisible = ref(false)

// 检查是否存在已保存的凭据
const savedCredentialsExist = ref(false);

// 清除已保存的凭据
const clearSavedCredentials = () => {
  localStorage.removeItem('saved_username');
  localStorage.removeItem('saved_password');
  localStorage.removeItem('auto_login'); // 确保移除可能存在的自动登录设置
  
  loginForm.value.remember = false;
  
  // 只清除密码，保留当前输入的用户名，便于重新输入
  loginForm.value.password = '';
  
  savedCredentialsExist.value = false;
  
  ElMessage.success('已清除保存的登录信息');
};

// 监听props更新本地状态
watch(() => props.show, (newVal) => {
  console.log('LoginDialog props.show changed:', newVal)
  visible.value = newVal
  if (newVal) {
    initLoginForm()
  }
})

// 监听本地状态更新props
watch(visible, (newVal) => {
  console.log('LoginDialog visible changed:', newVal)
  emit('update:show', newVal)
})

// 定义响应式状态
const loginForm = ref({
  username: '',
  password: '',
  remember: false
})

// 初始化登录表单，尝试加载保存的用户名和密码
const initLoginForm = () => {
  const savedUsername = localStorage.getItem('saved_username')
  const savedEncryptedPassword = localStorage.getItem('saved_password')
  
  // 检查是否存在已保存的凭据
  savedCredentialsExist.value = !!savedUsername;
  
  if (savedUsername) {
    loginForm.value.username = savedUsername
    loginForm.value.remember = true
    
    // 如果有保存的密码，也一并加载并解密
    if (savedEncryptedPassword) {
      loginForm.value.password = decryptPassword(savedEncryptedPassword)
    }
  }
}

// 打开对话框时的处理
const onDialogOpened = () => {
  console.log('LoginDialog opened')
}

// 弹窗关闭时的处理
const onDialogClosed = () => {
  console.log('LoginDialog closed')
  // 只有在未勾选记住密码时才清空密码
  if (!loginForm.value.remember) {
    loginForm.value.password = '' // 清空密码
  }
}

// 登录处理
const handleLogin = async () => {
  console.log('Attempting login with:', loginForm.value.username)
  
  if (!loginForm.value.username || !loginForm.value.password) {
    ElMessage.warning('请输入用户名和密码')
    return
  }

  loading.value = true
  try {
    // 首先尝试使用v1版本的API
    let response = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: loginForm.value.username,
        password: loginForm.value.password
      })
    })

    let data = await response.json()
    console.log('Login response:', data)
    
    // 如果v1 API返回401，尝试使用旧版API登录
    if (response.status === 401) {
      console.log('尝试降级到旧版API...')
      response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: loginForm.value.username,
          password: loginForm.value.password
        })
      })
      data = await response.json()
      console.log('旧版API登录结果:', data)
    }
    
    if (!response.ok) {
      throw new Error(data.message || '登录失败')
    }

    // 同时支持accessToken和token格式，以兼容新旧API
    const token = data.accessToken || data.token;
    if (token && data.user) {
      // 根据用户选择是否记住密码
      if (loginForm.value.remember) {
        // 如果勾选了记住密码，同时保存用户名和加密后的密码
        localStorage.setItem('saved_username', loginForm.value.username)
        localStorage.setItem('saved_password', encryptPassword(loginForm.value.password))
      } else {
        // 如果未勾选，清除保存的信息
        localStorage.removeItem('saved_username')
        localStorage.removeItem('saved_password')
      }
      
      // 使用authState登录，传入刷新令牌
      console.log('使用authState进行登录')
      authState.login(token, data.user, data.refreshToken);
      
      // 立即显示成功信息
      ElMessage.success('登录成功')
      
      // 检查是否是管理员
      const isAdmin = data.user.role === 'admin'
      console.log('用户角色:', data.user.role, '是管理员:', isAdmin)
      
      // 关闭弹窗
      visible.value = false
      
      // 发送登录成功事件
      emit('login-success', {
        user: data.user,
        isAdmin,
        redirectUrl: props.redirectUrl || (isAdmin ? '/admin' : '/')
      })
      
      // 重定向到指定页面
      if (props.redirectUrl) {
        router.push(props.redirectUrl)
      } else if (isAdmin) {
        router.push('/admin')
      }
    } else {
      throw new Error('登录返回数据格式错误')
    }
  } catch (error) {
    console.error('Login error:', error)
    ElMessage.error(error.message || '登录失败，请检查用户名和密码')
    // 发送登录失败事件
    emit('login-error', error)
  } finally {
    loading.value = false
  }
}

// 初始化
initLoginForm()
</script>

<style scoped>
/* 自定义对话框样式 */
:deep(.login-dialog) {
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
}

:deep(.el-dialog__header) {
  padding: 15px 20px;
  margin: 0;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(90deg, #2575fc 0%, #6a11cb 100%);
}

:deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
}

:deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white;
}

:deep(.el-dialog__body) {
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.login-box {
  position: relative;
  z-index: 1;
}

.login-header {
  text-align: center;
  margin-bottom: 15px;
}

.login-header h2 {
  margin: 0;
  color: #333;
  font-size: 20px;
  font-weight: 600;
}

.login-subtitle {
  margin: 5px 0 0;
  color: #666;
  font-size: 14px;
}

.login-form {
  padding: 10px 0;
}

.password-icon {
  cursor: pointer;
  font-size: 16px;
  color: #909399;
}

.login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.clear-saved-btn {
  padding: 0;
  font-size: 12px;
  color: #909399;
}

.clear-saved-btn:hover {
  color: #409EFF;
}

.security-tip {
  font-size: 12px;
  color: #E6A23C;
  text-align: left;
  margin-top: -10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.security-tip i {
  margin-right: 5px;
  font-size: 14px;
}

.login-button {
  width: 100%;
  height: 44px;
  border-radius: 22px;
  font-size: 16px;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #2575fc 0%, #6a11cb 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(106, 17, 203, 0.3);
  transition: all 0.3s ease;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(106, 17, 203, 0.4);
}

/* 响应式调整 */
@media (max-width: 480px) {
  :deep(.el-dialog) {
    width: 90% !important;
    margin: 0 auto !important;
  }
}
</style> 